import { applyJoinFields } from "./utils";
import { importForm001Fields } from "./importForm001";
import type { PdfFormDefinition } from "./types";

/**
 * Field coordinates for Форма 001/о (Ukrainian military medical evacuation card).
 *
 * Coordinates are in PDF points, bottom-left origin (y increases upward).
 * Add fields here as coordinates are identified from the dump output.
 * Commands and payloads are defined separately in fieldMap.ts.
 */
export const form001: PdfFormDefinition = {
  id: "form001",
  bodyInjuryPage: 1,
  joinFields: [
    { key: "patientName", sources: ["patientName", "patientName2"] },
    // "16 33" + "27 01" + "26" → "16 33 27 01 26" → parsed as HH MM DD MM YY in fieldMap
    { key: "eventDate", sources: ["eventTime", "eventDateDayMonth", "eventDateYear"] },
    // Same format, row below (Прибуття)
    { key: "arrivalDate", sources: ["arrivalTime", "arrivalDateDayMonth", "arrivalDateYear"] },
    // Same format — prior care date/time (section 2.2)
    { key: "priorCareDate", sources: ["prior-care-time", "prior-care-date-daymonth", "prior-care-date-year"] },
    // "12" + "07" + "1991" → "12 07 1991" → parsed as DD MM YYYY → ISO date string
    { key: "dateOfBirth", sources: ["dateOfBirthDay", "dateOfBirthMonth", "dateOfBirthYear"] },
    // Section 3.5 — trauma notes (two lines)
    { key: "p1-trauma-notes", sources: ["p1-trauma-notes", "p1-trauma-notes2"], separator: "\n" },
    // Section 11 — general notes (four lines)
    { key: "notes", sources: ["p1-sec11-notes-0", "p1-sec11-notes-1", "p1-sec11-notes-2", "p1-sec11-notes-3"], separator: "\n" },
    // Section 2.6 — antibiotic detail spans three lines
    { key: "p1-prior-medAntibioticDetail", sources: ["p1-prior-medAntibioticDetail", "p1-prior-medAntibioticDetail2", "p1-prior-medAntibioticDetail3"], separator: "\n" },
    // Section 4.1 — R1 tq limb grid times (HH + MM stored as separate PDF items)
    { key: "p1-tq-rue-app",  sources: ["p1-tq-rue-app-H",  "p1-tq-rue-app-M"] },
    { key: "p1-tq-rue-rem",  sources: ["p1-tq-rue-rem-H",  "p1-tq-rue-rem-M"] },
    { key: "p1-tq-rue-conv", sources: ["p1-tq-rue-conv-H", "p1-tq-rue-conv-M"] },
    { key: "p1-tq-rue-repo", sources: ["p1-tq-rue-repo-H", "p1-tq-rue-repo-M"] },
    { key: "p1-tq-lue-app",  sources: ["p1-tq-lue-app-H",  "p1-tq-lue-app-M"] },
    { key: "p1-tq-lue-rem",  sources: ["p1-tq-lue-rem-H",  "p1-tq-lue-rem-M"] },
    { key: "p1-tq-lue-conv", sources: ["p1-tq-lue-conv-H", "p1-tq-lue-conv-M"] },
    { key: "p1-tq-lue-repo", sources: ["p1-tq-lue-repo-H", "p1-tq-lue-repo-M"] },
    { key: "p1-tq-rle-app",  sources: ["p1-tq-rle-app-H",  "p1-tq-rle-app-M"] },
    { key: "p1-tq-rle-rem",  sources: ["p1-tq-rle-rem-H",  "p1-tq-rle-rem-M"] },
    { key: "p1-tq-rle-conv", sources: ["p1-tq-rle-conv-H", "p1-tq-rle-conv-M"] },
    { key: "p1-tq-rle-repo", sources: ["p1-tq-rle-repo-H", "p1-tq-rle-repo-M"] },
    { key: "p1-tq-lle-app",  sources: ["p1-tq-lle-app-H",  "p1-tq-lle-app-M"] },
    { key: "p1-tq-lle-rem",  sources: ["p1-tq-lle-rem-H",  "p1-tq-lle-rem-M"] },
    { key: "p1-tq-lle-conv", sources: ["p1-tq-lle-conv-H", "p1-tq-lle-conv-M"] },
    { key: "p1-tq-lle-repo", sources: ["p1-tq-lle-repo-H", "p1-tq-lle-repo-M"] },
    // Section 4.1 — AT/VT tourniquet times (two boxes joined as free text)
    { key: "p1-r1care-tourniquetATTime1", sources: ["p1-r1care-tourniquetATTime1H", "p1-r1care-tourniquetATTime1M"] },
    { key: "p1-r1care-tourniquetATTime2", sources: ["p1-r1care-tourniquetATTime2H", "p1-r1care-tourniquetATTime2M"] },
    { key: "p1-r1care-tourniquetATTimes1", sources: ["p1-r1care-tourniquetATTime1", "p1-r1care-tourniquetATTime2"] },
    // Section 18.1 — R2 tq application times (HH + MM stored as separate PDF items)
    { key: "p2-tq-rue-app-time", sources: ["p2-tq-rue-app-H", "p2-tq-rue-app-M"] },
    { key: "p2-tq-lue-app-time", sources: ["p2-tq-lue-app-H", "p2-tq-lue-app-M"] },
    { key: "p2-tq-rle-app-time", sources: ["p2-tq-rle-app-H", "p2-tq-rle-app-M"] },
    { key: "p2-tq-lle-app-time", sources: ["p2-tq-lle-app-H", "p2-tq-lle-app-M"] },
    // Section 2.5 — prior care tq grid times (HH + MM stored as separate PDF items)
    { key: "p1-prior-tourniquetRUEApp",  sources: ["p1-prior-tourniquetRUEApp-H",  "p1-prior-tourniquetRUEApp-M"] },
    { key: "p1-prior-tourniquetRUERem",  sources: ["p1-prior-tourniquetRUERem-H",  "p1-prior-tourniquetRUERem-M"] },
    { key: "p1-prior-tourniquetRUEConv", sources: ["p1-prior-tourniquetRUEConv-H", "p1-prior-tourniquetRUEConv-M"] },
    { key: "p1-prior-tourniquetRUERepo", sources: ["p1-prior-tourniquetRUERepo-H", "p1-prior-tourniquetRUERepo-M"] },
    { key: "p1-prior-tourniquetLUEApp",  sources: ["p1-prior-tourniquetLUEApp-H",  "p1-prior-tourniquetLUEApp-M"] },
    { key: "p1-prior-tourniquetLUERem",  sources: ["p1-prior-tourniquetLUERem-H",  "p1-prior-tourniquetLUERem-M"] },
    { key: "p1-prior-tourniquetLUEConv", sources: ["p1-prior-tourniquetLUEConv-H", "p1-prior-tourniquetLUEConv-M"] },
    { key: "p1-prior-tourniquetLUERepo", sources: ["p1-prior-tourniquetLUERepo-H", "p1-prior-tourniquetLUERepo-M"] },
    { key: "p1-prior-tourniquetRLEApp",  sources: ["p1-prior-tourniquetRLEApp-H",  "p1-prior-tourniquetRLEApp-M"] },
    { key: "p1-prior-tourniquetRLERem",  sources: ["p1-prior-tourniquetRLERem-H",  "p1-prior-tourniquetRLERem-M"] },
    { key: "p1-prior-tourniquetRLEConv", sources: ["p1-prior-tourniquetRLEConv-H", "p1-prior-tourniquetRLEConv-M"] },
    { key: "p1-prior-tourniquetRLERepo", sources: ["p1-prior-tourniquetRLERepo-H", "p1-prior-tourniquetRLERepo-M"] },
    { key: "p1-prior-tourniquetLLEApp",  sources: ["p1-prior-tourniquetLLEApp-H",  "p1-prior-tourniquetLLEApp-M"] },
    { key: "p1-prior-tourniquetLLERem",  sources: ["p1-prior-tourniquetLLERem-H",  "p1-prior-tourniquetLLERem-M"] },
    { key: "p1-prior-tourniquetLLEConv", sources: ["p1-prior-tourniquetLLEConv-H", "p1-prior-tourniquetLLEConv-M"] },
    { key: "p1-prior-tourniquetLLERepo", sources: ["p1-prior-tourniquetLLERepo-H", "p1-prior-tourniquetLLERepo-M"] },
    { key: "p1-prior-tourniquetATApp",   sources: ["p1-prior-tourniquetATApp-H",   "p1-prior-tourniquetATApp-M"] },
    { key: "p1-prior-tourniquetATRem",   sources: ["p1-prior-tourniquetATRem-H",   "p1-prior-tourniquetATRem-M"] },
    // Section 4.11 — anesthesia regional detail (two lines)
    { key: "p1-r1care-anesthesiaDetail", sources: ["p1-r1care-anesthesiaDetail", "p1-r1care-anesthesiaDetail2"], separator: "\n" },
    // Section 18.14 — R2 anesthesia notes (two lines)
    { key: "r2-anesthesiaNotes", sources: ["r2-anesthesiaNotes", "r2-anesthesiaNotes2"], separator: "\n" },
    // Section 8 — death time + date (HH MM DD MM YY) → parseDMY → timestamp
    { key: "p1-deathDate", sources: ["p1-death-time", "p1-death-date-daymonth", "p1-death-date-year"] },
    // Section 11 — notes textarea (4 lines, lineHeight≈10.5pt, y_pdf: 135→104)
    // Section 12 — diagnosis textarea (5 lines, lineHeight≈10pt, y_pdf: 85→45)
    { key: "p1-diagnosis", sources: ["p1-diagnosis-0", "p1-diagnosis-1", "p1-diagnosis-2", "p1-diagnosis-3", "p1-diagnosis-4"], separator: "\n" },
    // Section 13 — discharge time + date (HH MM DD MM YY) → parseDMY → timestamp
    { key: "p1-discharge-time",     sources: ["p1-discharge-timeH", "p1-discharge-timeM"] },
    { key: "p1-discharge-daymonth", sources: ["p1-discharge-day",   "p1-discharge-month"] },
    { key: "p1-dischargeDate", sources: ["p1-discharge-time", "p1-discharge-daymonth", "p1-discharge-year"] },
    // ── Page 2 joins ──────────────────────────────────────────────────────────
    // Section 15 — R2 arrival date/time
    { key: "r2-arrivalDate", sources: ["r2-arrival-time", "r2-arrival-daymonth", "r2-arrival-year"] },
    // Section 18.15 — R2 surgery start/end date/time
    { key: "r2-surgStartDate", sources: ["r2-surg-start-H", "r2-surg-start-M", "r2-surg-start-day", "r2-surg-start-month", "r2-surg-start-year"] },
    { key: "r2-surgEndDate",   sources: ["r2-surg-end-H",   "r2-surg-end-M",   "r2-surg-end-day",   "r2-surg-end-month",   "r2-surg-end-year"] },
    // Section 22 — R2 death date/time
    { key: "r2-deathDate", sources: ["r2-death-time", "r2-death-date-daymonth", "r2-death-date-year"] },
    // Section 24 — R2 notes (4 lines)
    { key: "r2-notes", sources: ["r2-notes-0", "r2-notes-1", "r2-notes-2", "r2-notes-3"], separator: "\n" },
    // Section 25 — R2 diagnosis (5 lines)
    { key: "r2-diagnosis", sources: ["r2-diagnosis-0", "r2-diagnosis-1", "r2-diagnosis-2", "r2-diagnosis-3", "r2-diagnosis-4"], separator: "\n" },
    // Section 26 — R2 discharge date/time (day and month are separate PDF items at x:169 and x:181)
    { key: "r2-discharge-time", sources: ["r2-discharge-timeH", "r2-discharge-timeM"] },
    { key: "r2-dischargeDate", sources: ["r2-discharge-time", "r2-discharge-day", "r2-discharge-month", "r2-discharge-year"] },
    // Section 10 — P1 med left times (HH + MM stored as separate PDF items)
    { key: "p1-med-left-0-time", sources: ["p1-med-left-0-timeH", "p1-med-left-0-timeM"] },
    { key: "p1-med-left-1-time", sources: ["p1-med-left-1-timeH", "p1-med-left-1-timeM"] },
    { key: "p1-med-left-2-time", sources: ["p1-med-left-2-timeH", "p1-med-left-2-timeM"] },
    { key: "p1-med-left-3-time", sources: ["p1-med-left-3-timeH", "p1-med-left-3-timeM"] },
    { key: "p1-med-left-4-time", sources: ["p1-med-left-4-timeH", "p1-med-left-4-timeM"] },
    // Section 10 — P1 med right times
    { key: "p1-med-right-0-time", sources: ["p1-med-right-0-timeH", "p1-med-right-0-timeM"] },
    { key: "p1-med-right-1-time", sources: ["p1-med-right-1-timeH", "p1-med-right-1-timeM"] },
    { key: "p1-med-right-2-time", sources: ["p1-med-right-2-timeH", "p1-med-right-2-timeM"] },
    { key: "p1-med-right-3-time", sources: ["p1-med-right-3-timeH", "p1-med-right-3-timeM"] },
    { key: "p1-med-right-4-time", sources: ["p1-med-right-4-timeH", "p1-med-right-4-timeM"] },
    // Section 23 — R2 med left times (HH + MM stored as separate PDF items)
    { key: "p2-med-left-0-time", sources: ["p2-med-left-0-timeH", "p2-med-left-0-timeM"] },
    { key: "p2-med-left-1-time", sources: ["p2-med-left-1-timeH", "p2-med-left-1-timeM"] },
    { key: "p2-med-left-2-time", sources: ["p2-med-left-2-timeH", "p2-med-left-2-timeM"] },
    { key: "p2-med-left-3-time", sources: ["p2-med-left-3-timeH", "p2-med-left-3-timeM"] },
    { key: "p2-med-left-4-time", sources: ["p2-med-left-4-timeH", "p2-med-left-4-timeM"] },
    // Section 23 — R2 med right times
    { key: "p2-med-right-0-time", sources: ["p2-med-right-0-timeH", "p2-med-right-0-timeM"] },
    { key: "p2-med-right-1-time", sources: ["p2-med-right-1-timeH", "p2-med-right-1-timeM"] },
    { key: "p2-med-right-2-time", sources: ["p2-med-right-2-timeH", "p2-med-right-2-timeM"] },
    { key: "p2-med-right-3-time", sources: ["p2-med-right-3-timeH", "p2-med-right-3-timeM"] },
    { key: "p2-med-right-4-time", sources: ["p2-med-right-4-timeH", "p2-med-right-4-timeM"] },
    // Section 16 — R2 additional info (two lines)
    { key: "r2-additionalInfo", sources: ["r2-additionalInfo-0", "r2-additionalInfo-1"], separator: "\n" },
    // Section 18.13 — Surgery description (6 lines)
    { key: "surg-description", sources: ["surg-description-0", "surg-description-1", "surg-description-2", "surg-description-3", "surg-description-4", "surg-description-5"], separator: "\n" },
  ],
  textFields: [
    // ── Card number (№) ───────────────────────────────────────────────────
    { key: "caseNumber", page: 1, x: 534, y: 817, xMin: 530 },
    // ── Section 1.1 — Patient identity ────────────────────────────────────
    { key: "patientName", page: 1, x: 37, y: 780 },
    // ── Section 1.3 — Event date+time (Подія Час/Дата) ────────────────────
    // Three raw fragments combined via joinFields → "eventDate" key (HH MM DD MM YY)
    { key: "eventTime", page: 1, x: 373, y: 780 },
    { key: "eventDateDayMonth", page: 1, x: 443, y: 780 },
    { key: "eventDateYear", page: 1, x: 486, y: 780 },
    // ── Section 1.3 — Arrival date+time (Прибуття Час/Дата) ──────────────
    // Same layout, row below
    { key: "arrivalTime", page: 1, x: 374, y: 769 },
    { key: "arrivalDateDayMonth", page: 1, x: 443, y: 769 },
    { key: "arrivalDateYear", page: 1, x: 486, y: 769 },
    // Second line of name (patronymic / overflow); combined with patientName in importFromPdf
    { key: "patientName2", page: 1, x: 23, y: 769 },
    { key: "rank", page: 1, x: 209, y: 780 },
    // ── Section 1.1 — Unit (В/частина) ────────────────────────────────────
    // Handled specially in importFromPdf (unit lookup/create flow); no fieldMap entry
    { key: "unitName", page: 1, x: 215, y: 770 },
    // ── Section 1.2 — ID ──────────────────────────────────────────────────
    // Between "ID" label (x:22) and "ДН" label (x:96)
    { key: "patientId", page: 1, x: 55, y: 747, xTol: 30 },
    // ── Section 1.2 — Date of birth (ДН) ─────────────────────────────────
    // Three fragments combined via joinFields → "dateOfBirth" (DD MM YYYY)
    { key: "dateOfBirthDay", page: 1, x: 114, y: 748 },
    { key: "dateOfBirthMonth", page: 1, x: 134, y: 748 },
    { key: "dateOfBirthYear", page: 1, x: 150, y: 748 },
    // ── Section 1.2 — Allergy details ────────────────────────────────────
    // After allergy yes/no checkboxes (end ~x:165)
    { key: "allergyDetails", page: 1, x: 194, y: 748, xTol: 30, yTol: 5, xMin: 190 },
    // ── Section 1.3 — Medical facility ────────────────────────────────────
    { key: "medicalFacility", page: 1, x: 392, y: 748, xMin: 392 },
    // ── Section 2.2 — Prior care time ────────────────────────────────────────
    { key: "prior-care-time",          page: 1, x: 47,  y: 715 },
    { key: "prior-care-date-daymonth", page: 1, x: 109, y: 715 },
    { key: "prior-care-date-year",     page: 1, x: 149, y: 715 },
    // ── Section 2.3 — Prior assessment vitals (y:695) ─────────────────────────
    { key: "p1-prior-assessRR",        page: 1, x: 36,  y: 695 },
    { key: "p1-prior-assessSpO2",      page: 1, x: 85,  y: 695 },
    { key: "p1-prior-assessPulse",     page: 1, x: 138, y: 695 },
    { key: "p1-prior-assessSystolic",  page: 1, x: 172, y: 695 },
    { key: "p1-prior-assessDiastolic", page: 1, x: 206, y: 695 },
    { key: "p1-prior-assessAVPU",      page: 1, x: 263, y: 695 },
    { key: "p1-prior-assessPain",      page: 1, x: 334, y: 695 },
    // ── Section 3.5 — Trauma notes (two lines, joined via joinFields) ──────────
    { key: "p1-trauma-notes",  page: 1, x: 56, y: 528, xTol: 250, xMin: 50 },
    { key: "p1-trauma-notes2", page: 1, x: 23, y: 517, xTol: 250 },
    // ── Section 2.6 — Medication detail text ─────────────────────────────────
    { key: "p1-prior-otherDetail",         page: 1, x: 110, y: 622, xTol: 60, xMin: 110 },
    { key: "p1-prior-medAnalgesicDetail",  page: 1, x: 73, y: 578, xTol: 60, xMin: 110 },
    { key: "p1-prior-medAntibioticDetail",  page: 1, x: 73, y: 566, xTol: 60, yTol: 5, xMin: 110 },
    { key: "p1-prior-medAntibioticDetail2", page: 1, x: 23, y: 555, xTol: 250 },
    { key: "p1-prior-medAntibioticDetail3", page: 1, x: 23, y: 543, xTol: 250, yTol: 5 },
    // ── Section 2.6 — Medication dose / route / time grid ────────────────────
    // Cols: Dose=262, Route=301, Time=334; Rows: TXA=590, Analgesic=577, Antibiotic=564, Extra=551
    { key: "p1-prior-medTXADose",          page: 1, x: 262, y: 590 },
    { key: "p1-prior-medTXARoute",         page: 1, x: 301, y: 590 },
    { key: "p1-prior-medTXATime",          page: 1, x: 334, y: 590 },
    { key: "p1-prior-medAnalgesicDose",    page: 1, x: 262, y: 578 },
    { key: "p1-prior-medAnalgesicRoute",   page: 1, x: 301, y: 578 },
    { key: "p1-prior-medAnalgesicTime",    page: 1, x: 334, y: 578 },
    { key: "p1-prior-medAntibioticDose",   page: 1, x: 262, y: 567 },
    { key: "p1-prior-medAntibioticRoute",  page: 1, x: 301, y: 567 },
    { key: "p1-prior-medAntibioticTime",   page: 1, x: 334, y: 567 },
    { key: "p1-prior-medExtraDose",        page: 1, x: 262, y: 555 },
    { key: "p1-prior-medExtraRoute",       page: 1, x: 301, y: 555 },
    { key: "p1-prior-medExtraTime",        page: 1, x: 334, y: 555 },
    // ── Section 4.1 — R1 tourniquet time grid ────────────────────────────
    // Cols: App=63/75, Rem=91/103, Conv=120/132, Repo=148/160 (H/M split; colons at 72/100/129/157)
    { key: "p1-tq-rue-app-H",  page: 1, x: 63,  y: 473 }, { key: "p1-tq-rue-app-M",  page: 1, x: 75,  y: 473 },
    { key: "p1-tq-rue-rem-H",  page: 1, x: 91,  y: 473 }, { key: "p1-tq-rue-rem-M",  page: 1, x: 103, y: 473 },
    { key: "p1-tq-rue-conv-H", page: 1, x: 120, y: 473 }, { key: "p1-tq-rue-conv-M", page: 1, x: 132, y: 473 },
    { key: "p1-tq-rue-repo-H", page: 1, x: 148, y: 473 }, { key: "p1-tq-rue-repo-M", page: 1, x: 160, y: 473 },
    { key: "p1-tq-lue-app-H",  page: 1, x: 63,  y: 462 }, { key: "p1-tq-lue-app-M",  page: 1, x: 75,  y: 462 },
    { key: "p1-tq-lue-rem-H",  page: 1, x: 91,  y: 462 }, { key: "p1-tq-lue-rem-M",  page: 1, x: 103, y: 462 },
    { key: "p1-tq-lue-conv-H", page: 1, x: 120, y: 462 }, { key: "p1-tq-lue-conv-M", page: 1, x: 132, y: 462 },
    { key: "p1-tq-lue-repo-H", page: 1, x: 148, y: 462 }, { key: "p1-tq-lue-repo-M", page: 1, x: 160, y: 462 },
    { key: "p1-tq-rle-app-H",  page: 1, x: 63,  y: 451 }, { key: "p1-tq-rle-app-M",  page: 1, x: 75,  y: 451 },
    { key: "p1-tq-rle-rem-H",  page: 1, x: 91,  y: 451 }, { key: "p1-tq-rle-rem-M",  page: 1, x: 103, y: 451 },
    { key: "p1-tq-rle-conv-H", page: 1, x: 120, y: 451 }, { key: "p1-tq-rle-conv-M", page: 1, x: 132, y: 451 },
    { key: "p1-tq-rle-repo-H", page: 1, x: 148, y: 451 }, { key: "p1-tq-rle-repo-M", page: 1, x: 160, y: 451 },
    { key: "p1-tq-lle-app-H",  page: 1, x: 63,  y: 440 }, { key: "p1-tq-lle-app-M",  page: 1, x: 75,  y: 440 },
    { key: "p1-tq-lle-rem-H",  page: 1, x: 91,  y: 440 }, { key: "p1-tq-lle-rem-M",  page: 1, x: 103, y: 440 },
    { key: "p1-tq-lle-conv-H", page: 1, x: 120, y: 440 }, { key: "p1-tq-lle-conv-M", page: 1, x: 132, y: 440 },
    { key: "p1-tq-lle-repo-H", page: 1, x: 148, y: 440 }, { key: "p1-tq-lle-repo-M", page: 1, x: 160, y: 440 },
    // ── Section 4.1 — AT/VT tourniquet times + detail ────────────────────
    { key: "p1-r1care-tourniquetATTime1H", page: 1, x: 63, y: 430, xTol: 5, xMin: 60 },
    { key: "p1-r1care-tourniquetATTime1M", page: 1, x: 75, y: 430, xMin: 74 },
    { key: "p1-r1care-tourniquetATTime2H", page: 1, x: 91, y: 430, xTol: 5, xMin: 88 },
    { key: "p1-r1care-tourniquetATTime2M", page: 1, x: 103, y: 430, xTol: 8,  xMin: 101 },
    { key: "p1-r1care-tourniquetDetail",   page: 1, x: 126, y: 430, xTol: 40, yTol: 5, xMin: 120 },
    // ── Section 4.4 — R1 thoracocentesis blood volume (мл) ───────────────
    { key: "p1-r1care-thoracoRightBloodMl", page: 1, x: 217, y: 430 },
    { key: "p1-r1care-thoracoLeftBloodMl",  page: 1, x: 217, y: 418 },
    // ── Section 4.4 — R1 O2 flow rate (л/хв) ────────────────────────────
    // Between "O2" (x:189) and "л/хв" (x:229)
    { key: "r1-o2", page: 1, x: 200, y: 452 },
    // ── Section 4.3 — R1 ETT tube size (cm) ──────────────────────────────
    // Between "ETT" (x:184, y:475) and "см" (x:216, y:473)
    { key: "r1-ettSize", page: 1, x: 207, y: 473 },
    // ── Section 4.6 — R1 hypothermia device/method ────────────────────────
    { key: "r1-hypothermiaDetail", page: 1, x: 357, y: 440, xTol: 80, xMin: 350 },
    // ── Section 4.11 — R1 anesthesia regional detail (two lines) ─────────
    { key: "p1-r1care-anesthesiaDetail",  page: 1, x: 505, y: 421, xTol: 100, yTol: 5, xMin: 490 },
    { key: "p1-r1care-anesthesiaDetail2", page: 1, x: 457, y: 410, xTol: 100, yTol: 5, xMin: 430 },
    // ── Section 5 — R1 vital signs grid ──────────────────────────────────
    // Cols (x from SVG%×595.28): time=20, rr=52, spo2=74, pulse=98, sys=125, dia=152, gcs=177, pain=213, temp=238, diuresis=264
    // Rows (y from 841.89×(1-SVG%)): row0=360, row1=349, row2=337
    { key: "p1-vs-0-time",     page: 1, x: 27,  y: 360 },
    { key: "p1-vs-0-rr",       page: 1, x: 52,  y: 360 },
    { key: "p1-vs-0-spo2",     page: 1, x: 74,  y: 360 },
    { key: "p1-vs-0-pulse",    page: 1, x: 98,  y: 360 },
    { key: "p1-vs-0-sys",      page: 1, x: 125, y: 360 },
    { key: "p1-vs-0-dia",      page: 1, x: 152, y: 360 },
    { key: "p1-vs-0-gcs",      page: 1, x: 177, y: 360 },
    { key: "p1-vs-0-pain",     page: 1, x: 213, y: 360 },
    { key: "p1-vs-0-temp",     page: 1, x: 238, y: 360 },
    { key: "p1-vs-0-diuresis", page: 1, x: 264, y: 360 },
    { key: "p1-vs-1-time",     page: 1, x: 27,  y: 349},
    { key: "p1-vs-1-rr",       page: 1, x: 52,  y: 349 },
    { key: "p1-vs-1-spo2",     page: 1, x: 74,  y: 349 },
    { key: "p1-vs-1-pulse",    page: 1, x: 98,  y: 349 },
    { key: "p1-vs-1-sys",      page: 1, x: 125, y: 349 },
    { key: "p1-vs-1-dia",      page: 1, x: 152, y: 349 },
    { key: "p1-vs-1-gcs",      page: 1, x: 177, y: 349 },
    { key: "p1-vs-1-pain",     page: 1, x: 213, y: 349 },
    { key: "p1-vs-1-temp",     page: 1, x: 238, y: 349 },
    { key: "p1-vs-1-diuresis", page: 1, x: 264, y: 349 },
    { key: "p1-vs-2-time",     page: 1, x: 27,  y: 337},
    { key: "p1-vs-2-rr",       page: 1, x: 52,  y: 337 },
    { key: "p1-vs-2-spo2",     page: 1, x: 74,  y: 337 },
    { key: "p1-vs-2-pulse",    page: 1, x: 98,  y: 337 },
    { key: "p1-vs-2-sys",      page: 1, x: 125, y: 337 },
    { key: "p1-vs-2-dia",      page: 1, x: 152, y: 337 },
    { key: "p1-vs-2-gcs",      page: 1, x: 177, y: 337 },
    { key: "p1-vs-2-pain",     page: 1, x: 213, y: 337 },
    { key: "p1-vs-2-temp",     page: 1, x: 238, y: 337 },
    { key: "p1-vs-2-diuresis", page: 1, x: 264, y: 337 },
    // ── Section 6 — R1 ventilation parameters grid ───────────────────────
    // Cols (x from SVG%): time=303, mode=339(xTol:50), rate=410, tv=437, fio2=466, peep=492, pip=517, etco2=542
    { key: "p1-vent-0-time",  page: 1, x: 311, y: 360 },
    { key: "p1-vent-0-mode",  page: 1, x: 339, y: 360, xTol: 50, yTol: 5, xMin: 325 },
    { key: "p1-vent-0-rate",  page: 1, x: 410, y: 360 },
    { key: "p1-vent-0-tv",    page: 1, x: 437, y: 360 },
    { key: "p1-vent-0-fio2",  page: 1, x: 466, y: 360 },
    { key: "p1-vent-0-peep",  page: 1, x: 492, y: 360 },
    { key: "p1-vent-0-pip",   page: 1, x: 517, y: 360 },
    { key: "p1-vent-0-etco2", page: 1, x: 542, y: 360 },
    { key: "p1-vent-1-time",  page: 1, x: 311, y: 349 },
    { key: "p1-vent-1-mode",  page: 1, x: 339, y: 349, xTol: 50, yTol: 5, xMin: 325 },
    { key: "p1-vent-1-rate",  page: 1, x: 410, y: 349 },
    { key: "p1-vent-1-tv",    page: 1, x: 437, y: 349 },
    { key: "p1-vent-1-fio2",  page: 1, x: 466, y: 349 },
    { key: "p1-vent-1-peep",  page: 1, x: 492, y: 349 },
    { key: "p1-vent-1-pip",   page: 1, x: 517, y: 349 },
    { key: "p1-vent-1-etco2", page: 1, x: 542, y: 349 },
    { key: "p1-vent-2-time",  page: 1, x: 311, y: 337 },
    { key: "p1-vent-2-mode",  page: 1, x: 339, y: 337, xTol: 50, yTol: 5, xMin: 325 },
    { key: "p1-vent-2-rate",  page: 1, x: 410, y: 337 },
    { key: "p1-vent-2-tv",    page: 1, x: 437, y: 337 },
    { key: "p1-vent-2-fio2",  page: 1, x: 466, y: 337 },
    { key: "p1-vent-2-peep",  page: 1, x: 492, y: 337 },
    { key: "p1-vent-2-pip",   page: 1, x: 517, y: 337 },
    { key: "p1-vent-2-etco2", page: 1, x: 542, y: 337 },
    // ── Section 7 — R1 CPR times ──────────────────────────────────────────
    // Handled via special block in importFromPdf (cpr.create entity pattern)
    { key: "p1-cpr-start", page: 1, x: 105, y: 321 },
    { key: "p1-cpr-end",   page: 1, x: 105, y: 310 },
    // ── Section 8 — Death ─────────────────────────────────────────────────
    // time+date joined as p1-deathDate → parseDMY; circumstances is free text
    { key: "p1-death-time",           page: 1, x: 226, y: 322 },
    { key: "p1-death-date-daymonth", page: 1, x: 289, y: 322 },
    { key: "p1-death-date-year",     page: 1, x: 336, y: 322 },
    { key: "p1-death-circumstances", page: 1, x: 404, y: 323, xTol: 100, xMin: 404 },
    // ── Section 9 — Diagnostics text ─────────────────────────────────────
    // Checkboxes handled in checkboxFields below; text content here
    { key: "p1-diag-xray",       page: 1, x: 175, y: 281, xTol: 50, xMin: 175 },
    { key: "p1-diag-ecg",        page: 1, x: 175, y: 270, xTol: 50, xMin: 175 },
    { key: "p1-diag-otoscopy-r", page: 1, x: 350, y: 281, xTol: 40, xMin: 350 },
    { key: "p1-diag-otoscopy-l", page: 1, x: 440, y: 281, xTol: 40, xMin: 440 },
    { key: "p1-diag-notes",      page: 1, x: 33,  y: 270, xTol: 60, xMin: 33  },
    { key: "p1-diag-other",      page: 1, x: 316, y: 270, xTol: 100, xMin: 316 },
    // ── Section 10 — R1 medications (left side, 5 rows) ──────────────────
    // Cols (x from SVG%×595.28): name=23(xTol60), dose=169, route=216, time=258
    // Rows (y from 841.89×(1−SVG%)): 235, 224, 214, 203, 191
    { key: "p1-med-left-0-name",  page: 1, x: 23,  y: 235, xTol: 60, yTol: 5 },
    { key: "p1-med-left-0-dose",   page: 1, x: 169, y: 235, yTol: 5 },
    { key: "p1-med-left-0-route",  page: 1, x: 216, y: 235, yTol: 5 },
    { key: "p1-med-left-0-timeH",  page: 1, x: 266, y: 235, yTol: 5 },
    { key: "p1-med-left-0-timeM",  page: 1, x: 278, y: 235, yTol: 5 },
    { key: "p1-med-left-1-name",   page: 1, x: 23,  y: 224, xTol: 60, yTol: 5 },
    { key: "p1-med-left-1-dose",   page: 1, x: 169, y: 224, yTol: 5 },
    { key: "p1-med-left-1-route",  page: 1, x: 216, y: 224, yTol: 5 },
    { key: "p1-med-left-1-timeH",  page: 1, x: 266, y: 224, yTol: 5 },
    { key: "p1-med-left-1-timeM",  page: 1, x: 278, y: 224, yTol: 5 },
    { key: "p1-med-left-2-name",   page: 1, x: 23,  y: 214, xTol: 60, yTol: 5 },
    { key: "p1-med-left-2-dose",   page: 1, x: 169, y: 214, yTol: 5 },
    { key: "p1-med-left-2-route",  page: 1, x: 216, y: 214, yTol: 5 },
    { key: "p1-med-left-2-timeH",  page: 1, x: 266, y: 214, yTol: 5 },
    { key: "p1-med-left-2-timeM",  page: 1, x: 278, y: 214, yTol: 5 },
    { key: "p1-med-left-3-name",   page: 1, x: 23,  y: 203, xTol: 60, yTol: 5 },
    { key: "p1-med-left-3-dose",   page: 1, x: 169, y: 203, yTol: 5 },
    { key: "p1-med-left-3-route",  page: 1, x: 216, y: 203, yTol: 5 },
    { key: "p1-med-left-3-timeH",  page: 1, x: 266, y: 203, yTol: 5 },
    { key: "p1-med-left-3-timeM",  page: 1, x: 278, y: 203, yTol: 5 },
    { key: "p1-med-left-4-name",   page: 1, x: 23,  y: 191, xTol: 60, yTol: 5 },
    { key: "p1-med-left-4-dose",   page: 1, x: 169, y: 191, yTol: 5 },
    { key: "p1-med-left-4-route",  page: 1, x: 216, y: 191, yTol: 5 },
    { key: "p1-med-left-4-timeH",  page: 1, x: 266, y: 191, yTol: 5 },
    { key: "p1-med-left-4-timeM",  page: 1, x: 278, y: 191, yTol: 5 },
    // ── Section 10 — R1 medications (right side, 5 rows) ─────────────────
    // Cols (x from SVG%×595.28): name=301(xTol60), dose=448, route=495, time=537
    { key: "p1-med-right-0-name",  page: 1, x: 301, y: 235, xTol: 60, yTol: 5, xMin: 301 },
    { key: "p1-med-right-0-dose",  page: 1, x: 448, y: 235, yTol: 5 },
    { key: "p1-med-right-0-route", page: 1, x: 495, y: 235, yTol: 5 },
    { key: "p1-med-right-0-timeH", page: 1, x: 545, y: 235, yTol: 5 },
    { key: "p1-med-right-0-timeM", page: 1, x: 557, y: 235, yTol: 5 },
    { key: "p1-med-right-1-name",  page: 1, x: 301, y: 224, xTol: 60, yTol: 5, xMin: 301 },
    { key: "p1-med-right-1-dose",  page: 1, x: 448, y: 224, yTol: 5 },
    { key: "p1-med-right-1-route", page: 1, x: 495, y: 224, yTol: 5 },
    { key: "p1-med-right-1-timeH", page: 1, x: 545, y: 224, yTol: 5 },
    { key: "p1-med-right-1-timeM", page: 1, x: 557, y: 224, yTol: 5 },
    { key: "p1-med-right-2-name",  page: 1, x: 301, y: 214, xTol: 60, yTol: 5, xMin: 301 },
    { key: "p1-med-right-2-dose",  page: 1, x: 448, y: 214, yTol: 5 },
    { key: "p1-med-right-2-route", page: 1, x: 495, y: 214, yTol: 5 },
    { key: "p1-med-right-2-timeH", page: 1, x: 545, y: 214, yTol: 5 },
    { key: "p1-med-right-2-timeM", page: 1, x: 557, y: 214, yTol: 5 },
    { key: "p1-med-right-3-name",  page: 1, x: 301, y: 203, xTol: 60, yTol: 5, xMin: 301 },
    { key: "p1-med-right-3-dose",  page: 1, x: 448, y: 203, yTol: 5 },
    { key: "p1-med-right-3-route", page: 1, x: 495, y: 203, yTol: 5 },
    { key: "p1-med-right-3-timeH", page: 1, x: 545, y: 203, yTol: 5 },
    { key: "p1-med-right-3-timeM", page: 1, x: 557, y: 203, yTol: 5 },
    { key: "p1-med-right-4-name",  page: 1, x: 301, y: 191, xTol: 60, yTol: 5, xMin: 301 },
    { key: "p1-med-right-4-dose",  page: 1, x: 448, y: 191, yTol: 5 },
    { key: "p1-med-right-4-route", page: 1, x: 495, y: 191, yTol: 5 },
    { key: "p1-med-right-4-timeH", page: 1, x: 545, y: 191, yTol: 5 },
    { key: "p1-med-right-4-timeM", page: 1, x: 557, y: 191, yTol: 5 },
    // ── Section 10.3 — Blood type text (AB0 / Rh factor) ─────────────────
    { key: "p1-blood-type", page: 1, x: 367, y: 175 },
    { key: "p1-rh-factor",  page: 1, x: 416, y: 175 },
    // ── Section 11 — Notes (textarea, 4 lines) ────────────────────────────
    // First line indented (x:82); subsequent lines at left margin (x:24)
    // Lines fed into the "notes" join (with section 3.5 lines)
    { key: "p1-sec11-notes-0", page: 1, x: 82, y: 161, xTol: 250, xMin: 50 },
    { key: "p1-sec11-notes-1", page: 1, x: 24, y: 151, xTol: 250 },
    { key: "p1-sec11-notes-2", page: 1, x: 24, y: 141, xTol: 250 },
    { key: "p1-sec11-notes-3", page: 1, x: 24, y: 131, xTol: 250 },
    // ── Section 12 — Diagnosis (textarea, 5 lines) ────────────────────────
    // First line indented (x:82); subsequent lines at left margin (x:23)
    // Lines joined via joinFields → "p1-diagnosis" → case.setDiagnosis
    { key: "p1-diagnosis-0", page: 1, x: 82, y: 118, xTol: 250, xMin: 50 },
    { key: "p1-diagnosis-1", page: 1, x: 23, y: 108, xTol: 250 },
    { key: "p1-diagnosis-2", page: 1, x: 23, y: 98,  xTol: 250 },
    { key: "p1-diagnosis-3", page: 1, x: 23, y: 87,  xTol: 250 },
    { key: "p1-diagnosis-4", page: 1, x: 24, y: 77,  xTol: 250 },
    // ── Section 13 — Discharge time / date ───────────────────────────────
    // Joined as p1-dischargeDate → parseDMY → case.setDischargeTime
    { key: "p1-discharge-timeH", page: 1, x: 110, y: 62 },
    { key: "p1-discharge-timeM", page: 1, x: 119, y: 62, xMin: 114 },
    { key: "p1-discharge-day",   page: 1, x: 171, y: 62 },
    { key: "p1-discharge-month", page: 1, x: 181, y: 62 },
    { key: "p1-discharge-year",  page: 1, x: 208, y: 62 },
    // ── Section 13 — Discharge destination ───────────────────────────────
    // Content starts after "(направлено)" label which ends ~x:355
    { key: "dischargeDestination", page: 1, x: 356, y: 61, xTol: 100, xMin: 352 },
    { key: "discharge-vehicle-id",   page: 1, x: 254, y: 38 },
    { key: "discharge-other-detail", page: 1, x: 488, y: 37 },
    // ── Section 14 — Medic name / facility ───────────────────────────────
    { key: "medicName", page: 1, x: 81, y: 21 },
    { key: "medicFacility", page: 1, x: 386, y: 21 },

    // ── Page 2 ────────────────────────────────────────────────────────────
    // ── Section 15 — R2 arrival time / date ──────────────────────────────
    { key: "r2-arrival-time",     page: 2, x: 130, y: 798 },
    { key: "r2-arrival-daymonth", page: 2, x: 192, y: 798 },
    { key: "r2-arrival-year",     page: 2, x: 234, y: 798 },
    // ── Section 16 — R2 patient examination text fields ──────────────────
    { key: "r2-exam-airway-type",       page: 2, x: 73,  y: 735, xTol: 40, xMin: 71 },  // ЗПДШ (тип)
    { key: "r2-exam-resp-support-type", page: 2, x: 68,  y: 713, xTol: 40, xMin: 65 }, // Resp support type text
    { key: "r2-exam-gcs",            page: 2, x: 459, y: 669, xTol: 20 },  // ШКГ score
    { key: "r2-exam-pupils-equal",   page: 2, x: 561, y: 669, xTol: 5  },  // Рівні: <, =, >, or -

    { key: "r2-exam-vision-r",       page: 2, x: 540, y: 650, xTol: 20, yTol: 5, xMin: 528 },  // Зір R (after D at x:520)
    { key: "r2-exam-vision-l",       page: 2, x: 487, y: 650, xTol: 20, yTol: 5, xMin: 465 },  // Зір L (after S at x:457)
    // ── Section 16 — R2 additional exam info ─────────────────────────────
    // "Додаткова інформація" single line; sample text at x:107, y:635
    { key: "r2-additionalInfo-0", page: 2, x: 107, y: 635, xTol: 250 },
    { key: "r2-additionalInfo-1", page: 2, x: 23,  y: 624, xTol: 250 },
    // ── Section 17 — R2 diagnostics text ─────────────────────────────────
    { key: "r2-diag-otoscopy-r", page: 2, x: 192, y: 584, xTol: 40, xMin: 188 },
    { key: "r2-diag-otoscopy-l", page: 2, x: 232, y: 583, xTol: 40, xMin: 228 },
    { key: "r2-diag-notes",      page: 2, x: 32,  y: 584, xTol: 60 },
    { key: "r2-diag-lab-notes",  page: 2, x: 127, y: 570, xTol: 60 },
    // CBC — filled PDF emits "Label: value" at y:598; bare labels sit at y:596.
    // Target y:598 so the combined item (dist≈0) beats the bare label (dist≈4).
    { key: "r2-diag-hg",  page: 2, x: 261, y: 598, xTol: 15 },
    { key: "r2-diag-hct", page: 2, x: 293, y: 598, xTol: 15 },
    { key: "r2-diag-er",  page: 2, x: 326, y: 598, xTol: 15 },
    { key: "r2-diag-wbc", page: 2, x: 357, y: 598, xTol: 15 },
    { key: "r2-diag-plt", page: 2, x: 384, y: 598, xTol: 15 },
    // Electrolytes — labels at y:585, combined values at y:586.
    { key: "r2-diag-na", page: 2, x: 261, y: 586, xTol: 15 },
    { key: "r2-diag-k",  page: 2, x: 292, y: 586, xTol: 15 },
    { key: "r2-diag-ca", page: 2, x: 316, y: 586, xTol: 15 },
    { key: "r2-diag-cl", page: 2, x: 345, y: 586, xTol: 15 },
    // Blood gas — pH value has no label prefix and sits at x:439 (label "pH" is at x:425).
    // pCO2/pO2/HCO3 combined values are at y:598; bare labels at y:596.
    { key: "r2-diag-ph",   page: 2, x: 439, y: 596, xTol: 13 },
    { key: "r2-diag-pco2", page: 2, x: 460, y: 598, xTol: 15 },
    { key: "r2-diag-po2",  page: 2, x: 498, y: 598, xTol: 15 },
    { key: "r2-diag-hco3", page: 2, x: 534, y: 598, xTol: 15 },
    // Biochemistry — labels at y:585, combined values at y:586.
    { key: "r2-diag-lactate",    page: 2, x: 378, y: 586, xTol: 15 },
    { key: "r2-diag-urea",       page: 2, x: 422, y: 586, xTol: 20 },
    { key: "r2-diag-creatinine", page: 2, x: 475, y: 586, xTol: 20 },
    { key: "r2-diag-glucose",    page: 2, x: 528, y: 586, xTol: 20 },
    // ── Section 18.15 — R2 surgery start / end ───────────────────────────
    // Values at y:359; separators "." at x:155/":" at x:243 excluded with xTol:5
    { key: "r2-surg-start-day",   page: 2, x: 148, y: 359, xTol: 5 },
    { key: "r2-surg-start-month", page: 2, x: 158, y: 359, xTol: 5 },
    { key: "r2-surg-start-year",  page: 2, x: 185, y: 359, xTol: 5 },
    { key: "r2-surg-start-H",     page: 2, x: 237, y: 359, xTol: 5 },
    { key: "r2-surg-start-M",     page: 2, x: 247, y: 359, xTol: 5 },
    // Values at y:359; separators "." at x:441/":" at x:529 excluded with xTol:5
    { key: "r2-surg-end-day",     page: 2, x: 435, y: 359, xTol: 5 },
    { key: "r2-surg-end-month",   page: 2, x: 444, y: 359, xTol: 5 },
    { key: "r2-surg-end-year",    page: 2, x: 471, y: 359, xTol: 5 },
    { key: "r2-surg-end-H",       page: 2, x: 523, y: 359, xTol: 5 },
    { key: "r2-surg-end-M",       page: 2, x: 533, y: 359, xTol: 5 },
    // ── Section 18.2 — R2 O2 flow rate (л/хв) ────────────────────────────
    // Between "O2" (x:138) and "л/хв" (x:174)
    { key: "r2-o2Flow", page: 2, x: 155, y: 542 },
    // ── Section 18.2 — R2 ETT tube size (cm) ─────────────────────────────
    // Between "ETT" (x:163) and "см" (x:191); tight tolerance
    { key: "r2-ettSize", page: 2, x: 180, y: 532, xTol: 8 },
    // ── Section 18.5 — R2 hypothermia device/method ───────────────────────
    // After "засіб" label (x:131); extends to ~x:290
    { key: "r2-hypothermiaDetail", page: 2, x: 150, y: 460, xTol: 15 },
    // ── Section 18.14 — R2 anesthesia notes ──────────────────────────────
    // Single underline after "Седація" checkbox row (last checkbox ~x:250)
    { key: "r2-anesthesiaNotes",  page: 2, x: 263, y: 382 },
    { key: "r2-anesthesiaNotes2", page: 2, x: 23,  y: 370 },
    // ── Section 19 — R2 vital signs grid ─────────────────────────────────
    { key: "r2-vs-0-time",     page: 2, x: 27,  y: 321 },
    { key: "r2-vs-0-rr",       page: 2, x: 57,  y: 321 },
    { key: "r2-vs-0-spo2",     page: 2, x: 74,  y: 321 },
    { key: "r2-vs-0-pulse",    page: 2, x: 98,  y: 321 },
    { key: "r2-vs-0-sys",      page: 2, x: 132, y: 321 },
    { key: "r2-vs-0-dia",      page: 2, x: 152, y: 321 },
    { key: "r2-vs-0-gcs",      page: 2, x: 177, y: 321 },
    { key: "r2-vs-0-pain",     page: 2, x: 213, y: 321 },
    { key: "r2-vs-0-temp",     page: 2, x: 238, y: 321 },
    { key: "r2-vs-0-diuresis", page: 2, x: 264, y: 321 },
    { key: "r2-vs-1-time",     page: 2, x: 27,  y: 311 },
    { key: "r2-vs-1-rr",       page: 2, x: 57,  y: 311 },
    { key: "r2-vs-1-spo2",     page: 2, x: 74,  y: 311 },
    { key: "r2-vs-1-pulse",    page: 2, x: 98,  y: 311 },
    { key: "r2-vs-1-sys",      page: 2, x: 132, y: 311 },
    { key: "r2-vs-1-dia",      page: 2, x: 152, y: 311 },
    { key: "r2-vs-1-gcs",      page: 2, x: 177, y: 311 },
    { key: "r2-vs-1-pain",     page: 2, x: 213, y: 311 },
    { key: "r2-vs-1-temp",     page: 2, x: 238, y: 311 },
    { key: "r2-vs-1-diuresis", page: 2, x: 264, y: 311 },
    { key: "r2-vs-2-time",     page: 2, x: 27,  y: 300 },
    { key: "r2-vs-2-rr",       page: 2, x: 57,  y: 300 },
    { key: "r2-vs-2-spo2",     page: 2, x: 74,  y: 300 },
    { key: "r2-vs-2-pulse",    page: 2, x: 98,  y: 300 },
    { key: "r2-vs-2-sys",      page: 2, x: 132, y: 300 },
    { key: "r2-vs-2-dia",      page: 2, x: 152, y: 300 },
    { key: "r2-vs-2-gcs",      page: 2, x: 177, y: 300 },
    { key: "r2-vs-2-pain",     page: 2, x: 213, y: 300 },
    { key: "r2-vs-2-temp",     page: 2, x: 238, y: 300 },
    { key: "r2-vs-2-diuresis", page: 2, x: 264, y: 300 },
    // ── Section 20 — R2 ventilation parameters grid ───────────────────────
    { key: "r2-vent-0-time",  page: 2, x: 311, y: 323 },
    { key: "r2-vent-0-mode",  page: 2, x: 361, y: 323, xTol: 50, yTol: 5, xMin: 340 },
    { key: "r2-vent-0-rate",  page: 2, x: 417, y: 323 },
    { key: "r2-vent-0-tv",    page: 2, x: 445, y: 323 },
    { key: "r2-vent-0-fio2",  page: 2, x: 470, y: 323 },
    { key: "r2-vent-0-peep",  page: 2, x: 493, y: 323 },
    { key: "r2-vent-0-pip",   page: 2, x: 521, y: 323 },
    { key: "r2-vent-0-etco2", page: 2, x: 546, y: 323 },
    { key: "r2-vent-1-time",  page: 2, x: 311, y: 311 },
    { key: "r2-vent-1-mode",  page: 2, x: 361, y: 311, xTol: 50, yTol: 5, xMin: 340 },
    { key: "r2-vent-1-rate",  page: 2, x: 417, y: 311 },
    { key: "r2-vent-1-tv",    page: 2, x: 445, y: 311 },
    { key: "r2-vent-1-fio2",  page: 2, x: 470, y: 311 },
    { key: "r2-vent-1-peep",  page: 2, x: 493, y: 311 },
    { key: "r2-vent-1-pip",   page: 2, x: 521, y: 311 },
    { key: "r2-vent-1-etco2", page: 2, x: 546, y: 311 },
    { key: "r2-vent-2-time",  page: 2, x: 311, y: 300 },
    { key: "r2-vent-2-mode",  page: 2, x: 361, y: 300, xTol: 50, yTol: 5, xMin: 340 },
    { key: "r2-vent-2-rate",  page: 2, x: 417, y: 300 },
    { key: "r2-vent-2-tv",    page: 2, x: 445, y: 300 },
    { key: "r2-vent-2-fio2",  page: 2, x: 470, y: 300 },
    { key: "r2-vent-2-peep",  page: 2, x: 493, y: 300 },
    { key: "r2-vent-2-pip",   page: 2, x: 521, y: 300 },
    { key: "r2-vent-2-etco2", page: 2, x: 546, y: 300 },
    // ── Section 21 — R2 CPR times ─────────────────────────────────────────
    { key: "r2-cpr-start", page: 2, x: 130, y: 285 },
    { key: "r2-cpr-end",   page: 2, x: 130, y: 273 },
    // ── Section 22 — R2 Death ─────────────────────────────────────────────
    { key: "r2-death-time",           page: 2, x: 257, y: 284 },
    { key: "r2-death-date-daymonth",  page: 2, x: 285, y: 284 },
    { key: "r2-death-date-year",      page: 2, x: 340, y: 284 },
    { key: "r2-death-circumstances",  page: 2, x: 420, y: 284, xTol: 50 },
    // ── Section 23 — R2 medications (left role=2, right role=12; 5 rows) ──
    { key: "p2-med-left-0-name",  page: 2, x: 23,  y: 235, xTol: 60 },
    { key: "p2-med-left-0-dose",   page: 2, x: 174, y: 235 },
    { key: "p2-med-left-0-route",  page: 2, x: 226, y: 235 },
    { key: "p2-med-left-0-timeH",  page: 2, x: 266, y: 235 },
    { key: "p2-med-left-0-timeM",  page: 2, x: 278, y: 235 },
    { key: "p2-med-left-1-name",   page: 2, x: 23,  y: 224, xTol: 60 },
    { key: "p2-med-left-1-dose",   page: 2, x: 174, y: 224 },
    { key: "p2-med-left-1-route",  page: 2, x: 226, y: 224 },
    { key: "p2-med-left-1-timeH",  page: 2, x: 266, y: 224 },
    { key: "p2-med-left-1-timeM",  page: 2, x: 278, y: 224 },
    { key: "p2-med-left-2-name",   page: 2, x: 23,  y: 214, xTol: 60 },
    { key: "p2-med-left-2-dose",   page: 2, x: 174, y: 214 },
    { key: "p2-med-left-2-route",  page: 2, x: 226, y: 214 },
    { key: "p2-med-left-2-timeH",  page: 2, x: 266, y: 214 },
    { key: "p2-med-left-2-timeM",  page: 2, x: 278, y: 214 },
    { key: "p2-med-left-3-name",   page: 2, x: 23,  y: 203, xTol: 60 },
    { key: "p2-med-left-3-dose",   page: 2, x: 174, y: 203 },
    { key: "p2-med-left-3-route",  page: 2, x: 226, y: 203 },
    { key: "p2-med-left-3-timeH",  page: 2, x: 266, y: 203 },
    { key: "p2-med-left-3-timeM",  page: 2, x: 278, y: 203 },
    { key: "p2-med-left-4-name",   page: 2, x: 23,  y: 191, xTol: 60 },
    { key: "p2-med-left-4-dose",   page: 2, x: 174, y: 191 },
    { key: "p2-med-left-4-route",  page: 2, x: 226, y: 191 },
    { key: "p2-med-left-4-timeH",  page: 2, x: 266, y: 191 },
    { key: "p2-med-left-4-timeM",  page: 2, x: 278, y: 191 },
    { key: "p2-med-right-0-name",  page: 2, x: 301, y: 235, xTol: 60 },
    { key: "p2-med-right-0-dose",  page: 2, x: 448, y: 235 },
    { key: "p2-med-right-0-route", page: 2, x: 495, y: 235 },
    { key: "p2-med-right-0-timeH", page: 2, x: 545, y: 235 },
    { key: "p2-med-right-0-timeM", page: 2, x: 557, y: 235 },
    { key: "p2-med-right-1-name",  page: 2, x: 301, y: 224, xTol: 60 },
    { key: "p2-med-right-1-dose",  page: 2, x: 448, y: 224 },
    { key: "p2-med-right-1-route", page: 2, x: 495, y: 224 },
    { key: "p2-med-right-1-timeH", page: 2, x: 545, y: 224 },
    { key: "p2-med-right-1-timeM", page: 2, x: 557, y: 224 },
    { key: "p2-med-right-2-name",  page: 2, x: 301, y: 214, xTol: 60 },
    { key: "p2-med-right-2-dose",  page: 2, x: 448, y: 214 },
    { key: "p2-med-right-2-route", page: 2, x: 495, y: 214 },
    { key: "p2-med-right-2-timeH", page: 2, x: 545, y: 214 },
    { key: "p2-med-right-2-timeM", page: 2, x: 557, y: 214 },
    { key: "p2-med-right-3-name",  page: 2, x: 301, y: 203, xTol: 60 },
    { key: "p2-med-right-3-dose",  page: 2, x: 448, y: 203 },
    { key: "p2-med-right-3-route", page: 2, x: 495, y: 203 },
    { key: "p2-med-right-3-timeH", page: 2, x: 545, y: 203 },
    { key: "p2-med-right-3-timeM", page: 2, x: 557, y: 203 },
    { key: "p2-med-right-4-name",  page: 2, x: 301, y: 191, xTol: 60 },
    { key: "p2-med-right-4-dose",  page: 2, x: 448, y: 191 },
    { key: "p2-med-right-4-route", page: 2, x: 495, y: 191 },
    { key: "p2-med-right-4-timeH", page: 2, x: 545, y: 191 },
    { key: "p2-med-right-4-timeM", page: 2, x: 557, y: 191 },
    // ── Section 23.2 — Blood type text / Rh factor (p2) ─────────────────
    { key: "p2-blood-type", page: 2, x: 382, y: 177 },
    { key: "p2-rh-factor",  page: 2, x: 432, y: 177 },
    // ── Section 24 — R2 notes (4 lines) ──────────────────────────────────
    { key: "r2-notes-0", page: 2, x: 82, y: 161, xTol: 250 },
    { key: "r2-notes-1", page: 2, x: 24, y: 151, xTol: 250 },
    { key: "r2-notes-2", page: 2, x: 24, y: 141, xTol: 250 },
    { key: "r2-notes-3", page: 2, x: 24, y: 131, xTol: 250 },
    // ── Section 25 — R2 diagnosis (5 lines) ──────────────────────────────
    { key: "r2-diagnosis-0", page: 2, x: 82, y: 118, xTol: 250 },
    { key: "r2-diagnosis-1", page: 2, x: 22, y: 108, xTol: 250 },
    { key: "r2-diagnosis-2", page: 2, x: 22, y: 98,  xTol: 250 },
    { key: "r2-diagnosis-3", page: 2, x: 22, y: 87,  xTol: 250 },
    { key: "r2-diagnosis-4", page: 2, x: 22, y: 77,  xTol: 250 },
    // ── Section 26 — R2 discharge time / date ────────────────────────────
    { key: "r2-discharge-timeH", page: 2, x: 110, y: 62 },
    { key: "r2-discharge-timeM", page: 2, x: 119, y: 62, xMin: 114 },
    { key: "r2-discharge-day",   page: 2, x: 171, y: 62 },
    { key: "r2-discharge-month", page: 2, x: 181, y: 62 },
    { key: "r2-discharge-year",  page: 2, x: 208, y: 62 },
    // ── Section 26 — R2 discharge destination / transport details ─────────
    { key: "r2-dischargeDestination",   page: 2, x: 380, y: 63,  xTol: 50 },
    { key: "p2-discharge-vehicle-id",   page: 2, x: 255, y: 39 },
    { key: "p2-discharge-other-detail", page: 2, x: 488, y: 38 },
    // ── Section 27 — R2 medic name / facility ─────────────────────────────
    { key: "r2-medicName", page: 2, x: 81, y: 21 },
    { key: "r2-medicFacility", page: 2, x: 386, y: 21 },
    // ── Section 18.13 — Surgery description (p2, 6 lines) ───────────────
    { key: "surg-description-0", page: 2, x: 117, y: 445, xTol: 250 },
    { key: "surg-description-1", page: 2, x: 23,  y: 434, xTol: 280 },
    { key: "surg-description-2", page: 2, x: 23,  y: 424, xTol: 280 },
    { key: "surg-description-3", page: 2, x: 24,  y: 413, xTol: 280 },
    { key: "surg-description-4", page: 2, x: 24,  y: 402, xTol: 280 },
    { key: "surg-description-5", page: 2, x: 24,  y: 392, xTol: 280 },

    // ── Section 2.5 — Tourniquet time grid ───────────────────────────────
    // Cols: App=232/244, Rem=265/277, Conv=298/310, Repo=330/342 (H/M split; colons at 241/274/307/339)
    { key: "p1-prior-tourniquetRUEApp-H",  page: 1, x: 232, y: 664 }, { key: "p1-prior-tourniquetRUEApp-M",  page: 1, x: 244, y: 664 },
    { key: "p1-prior-tourniquetRUERem-H",  page: 1, x: 265, y: 664 }, { key: "p1-prior-tourniquetRUERem-M",  page: 1, x: 277, y: 664 },
    { key: "p1-prior-tourniquetRUEConv-H", page: 1, x: 298, y: 664 }, { key: "p1-prior-tourniquetRUEConv-M", page: 1, x: 310, y: 664 },
    { key: "p1-prior-tourniquetRUERepo-H", page: 1, x: 330, y: 664 }, { key: "p1-prior-tourniquetRUERepo-M", page: 1, x: 342, y: 664 },
    { key: "p1-prior-tourniquetLUEApp-H",  page: 1, x: 232, y: 653 }, { key: "p1-prior-tourniquetLUEApp-M",  page: 1, x: 244, y: 653 },
    { key: "p1-prior-tourniquetLUERem-H",  page: 1, x: 265, y: 653 }, { key: "p1-prior-tourniquetLUERem-M",  page: 1, x: 277, y: 653 },
    { key: "p1-prior-tourniquetLUEConv-H", page: 1, x: 298, y: 653 }, { key: "p1-prior-tourniquetLUEConv-M", page: 1, x: 310, y: 653 },
    { key: "p1-prior-tourniquetLUERepo-H", page: 1, x: 330, y: 653 }, { key: "p1-prior-tourniquetLUERepo-M", page: 1, x: 342, y: 653 },
    { key: "p1-prior-tourniquetRLEApp-H",  page: 1, x: 232, y: 642 }, { key: "p1-prior-tourniquetRLEApp-M",  page: 1, x: 244, y: 642 },
    { key: "p1-prior-tourniquetRLERem-H",  page: 1, x: 265, y: 642 }, { key: "p1-prior-tourniquetRLERem-M",  page: 1, x: 277, y: 642 },
    { key: "p1-prior-tourniquetRLEConv-H", page: 1, x: 298, y: 642 }, { key: "p1-prior-tourniquetRLEConv-M", page: 1, x: 310, y: 642 },
    { key: "p1-prior-tourniquetRLERepo-H", page: 1, x: 330, y: 642 }, { key: "p1-prior-tourniquetRLERepo-M", page: 1, x: 342, y: 642 },
    { key: "p1-prior-tourniquetLLEApp-H",  page: 1, x: 232, y: 631 }, { key: "p1-prior-tourniquetLLEApp-M",  page: 1, x: 244, y: 631 },
    { key: "p1-prior-tourniquetLLERem-H",  page: 1, x: 265, y: 631 }, { key: "p1-prior-tourniquetLLERem-M",  page: 1, x: 277, y: 631 },
    { key: "p1-prior-tourniquetLLEConv-H", page: 1, x: 298, y: 631 }, { key: "p1-prior-tourniquetLLEConv-M", page: 1, x: 310, y: 631 },
    { key: "p1-prior-tourniquetLLERepo-H", page: 1, x: 330, y: 631 }, { key: "p1-prior-tourniquetLLERepo-M", page: 1, x: 342, y: 631 },
    { key: "p1-prior-tourniquetATApp-H",   page: 1, x: 232, y: 620 }, { key: "p1-prior-tourniquetATApp-M",   page: 1, x: 244, y: 620 },
    { key: "p1-prior-tourniquetATRem-H",   page: 1, x: 265, y: 620 }, { key: "p1-prior-tourniquetATRem-M",   page: 1, x: 277, y: 620 },
    { key: "p1-prior-tourniquetDetail",   page: 1, x: 304, y: 621, xTol: 60, xMin: 300 },
    // ── Section 18.1 — R2 tourniquet application text + time inputs ───────
    { key: "p2-tq-rue-app",   page: 2, x: 47, y: 531, yTol: 5, xMin: 44 },
    { key: "p2-tq-lue-app",   page: 2, x: 46, y: 519, yTol: 5, xMin: 44 },
    { key: "p2-tq-rle-app",   page: 2, x: 47, y: 507, yTol: 5, xMin: 44 },
    { key: "p2-tq-lle-app",   page: 2, x: 47, y: 496, yTol: 5, xMin: 44 },
    { key: "p2-tq-rue-app-H", page: 2, x: 92,  y: 530, xTol: 5, xMin: 89 },
    { key: "p2-tq-rue-app-M", page: 2, x: 104, y: 530, xTol: 5, xMin: 100 },
    { key: "p2-tq-lue-app-H", page: 2, x: 93,  y: 517, xTol: 5, xMin: 90 },
    { key: "p2-tq-lue-app-M", page: 2, x: 105, y: 517, xTol: 5, xMin: 100 },
    { key: "p2-tq-rle-app-H", page: 2, x: 91,  y: 507, xTol: 5, xMin: 88 },
    { key: "p2-tq-rle-app-M", page: 2, x: 103, y: 507, xTol: 5, xMin: 100 },
    { key: "p2-tq-lle-app-H", page: 2, x: 91,  y: 496, xTol: 5, xMin: 88 },
    { key: "p2-tq-lle-app-M", page: 2, x: 103, y: 496, xTol: 5, xMin: 100 },

    // ── Section 10.2 — Tetanus prophylaxis text ───────────────────────────
    { key: "p1-tetanusProphylaxis", page: 1, x: 110, y: 175 },
  ],
  checkboxFields: [
    // ── Section 1.1 — Sex ──────────────────────────────────────────────────
    { key: "sex-unknown", page: 1, x: 25, y: 760 },
    { key: "sex-male", page: 1, x: 81, y: 760 },
    { key: "sex-female", page: 1, x: 95, y: 760 },

    // ── Section 1.1 — Patient category (Ц/В/П) ───────────────────────────
    { key: "category-civilian", page: 1, x: 117, y: 760 },
    { key: "category-military", page: 1, x: 133, y: 760 },
    { key: "category-police", page: 1, x: 149, y: 760 },

    // ── Section 1.2 — Allergy ─────────────────────────────────────────────
    { key: "allergy-yes", page: 1, x: 175, y: 750 },
    { key: "allergy-no", page: 1, x: 214, y: 760 },
    { key: "allergy-unknown", page: 1, x: 234, y: 760 },

    // ── Section 1.3 — Transport type ──────────────────────────────────────
    { key: "transport-casevac", page: 1, x: 298, y: 760 },
    { key: "transport-mmpm", page: 1, x: 337, y: 760 },
    { key: "transport-medevac", page: 1, x: 375, y: 760 },
    { key: "transport-other", page: 1, x: 421, y: 760 },

    // ── Section 1.3 — Medical role ────────────────────────────────────────
    { key: "role-1", page: 1, x: 460, y: 760 },
    { key: "role-2", page: 1, x: 473, y: 760 },
    { key: "role-3", page: 1, x: 488, y: 760 },
    { key: "role-4", page: 1, x: 502, y: 760 },

    // ── Section 1.4 — Triage category ─────────────────────────────────────
    { key: "triage-I", page: 1, x: 523, y: 760 },
    { key: "triage-II", page: 1, x: 539, y: 760 },
    { key: "triage-III", page: 1, x: 523, y: 749 },
    { key: "triage-IV", page: 1, x: 544, y: 749 },

    // ── Section 2.4 — Prior care procedures ──────────────────────────────
    { key: "prior-bandage", page: 1, x: 24, y: 677 },
    { key: "prior-packing", page: 1, x: 68, y: 677 },
    { key: "prior-oxygen", page: 1, x: 132, y: 677 },
    { key: "prior-ofp", page: 1, x: 153, y: 677 },
    { key: "prior-nfp", page: 1, x: 24, y: 667 },
    { key: "prior-ngp", page: 1, x: 53, y: 667 },
    { key: "prior-ett", page: 1, x: 81, y: 667 },
    { key: "prior-cricothyrotomy", page: 1, x: 107, y: 667 },
    { key: "prior-needleDecompression", page: 1, x: 24, y: 656 },
    { key: "prior-occlusiveDressing", page: 1, x: 109, y: 656 },
    { key: "prior-bvm", page: 1, x: 24, y: 646 },
    { key: "prior-ivAccess", page: 1, x: 135, y: 646 },
    { key: "prior-ioAccess", page: 1, x: 158, y: 646 },
    { key: "prior-hypothermiaPrevention", page: 1, x: 24, y: 635 },
    { key: "prior-immobilization", page: 1, x: 125, y: 635 },
    { key: "prior-eyeShield", page: 1, x: 24, y: 624 },
    { key: "prior-other", page: 1, x: 85, y: 624 },

    // ── Section 2.5 — Tourniquet limbs ───────────────────────────────────
    { key: "prior-tourniquetRUE", page: 1, x: 192, y: 667 },
    { key: "prior-tourniquetLUE", page: 1, x: 192, y: 656 },
    { key: "prior-tourniquetRLE", page: 1, x: 192, y: 646 },
    { key: "prior-tourniquetLLE", page: 1, x: 192, y: 635 },
    { key: "prior-tourniquetAT", page: 1, x: 192, y: 624 },
    { key: "prior-tourniquetVT", page: 1, x: 210, y: 624 },

    // ── Section 2.2 — Prior care provider ────────────────────────────────
    { key: "prior-provider-self", page: 1, x: 175, y: 719 },
    { key: "prior-provider-nonpro", page: 1, x: 195, y: 719 },
    { key: "prior-provider-medpro", page: 1, x: 217, y: 719 },
    { key: "prior-provider-unknown", page: 1, x: 248, y: 719 },
    { key: "prior-provider-none", page: 1, x: 293, y: 719 },

    // ── Section 2.6 — Prior care medications ─────────────────────────────
    { key: "prior-medPillPack", page: 1, x: 25, y: 603 },
    { key: "prior-medTXA", page: 1, x: 25, y: 592 },
    { key: "prior-medAnalgesic", page: 1, x: 25, y: 581 },
    { key: "prior-medAntibiotic", page: 1, x: 25, y: 569 },

    // ── Section 4.1 — R1 tourniquet limbs ────────────────────────────────
    { key: "r1-tourniquetRUE", page: 1, x: 24, y: 475 },
    { key: "r1-tourniquetLUE", page: 1, x: 24, y: 465 },
    { key: "r1-tourniquetRLE", page: 1, x: 24, y: 455 },
    { key: "r1-tourniquetLLE", page: 1, x: 24, y: 444 },
    { key: "r1-tourniquetAT", page: 1, x: 24, y: 433 },
    { key: "r1-tourniquetVT", page: 1, x: 43, y: 433 },

    // ── Section 4.2 — R1 wound care ──────────────────────────────────────
    { key: "r1-bandage", page: 1, x: 25, y: 410 },
    { key: "r1-pressureBandage", page: 1, x: 66, y: 410 },
    { key: "r1-woundCare", page: 1, x: 137, y: 410 },
    { key: "r1-redressing", page: 1, x: 25, y: 399 },
    { key: "r1-packing", page: 1, x: 75, y: 399 },
    { key: "r1-occlusiveRight", page: 1, x: 255, y: 410 },
    { key: "r1-occlusiveLeft", page: 1, x: 270, y: 410 },
    { key: "r1-needleDecompRight", page: 1, x: 261, y: 399 },
    { key: "r1-needleDecompLeft", page: 1, x: 276, y: 399 },

    // ── Section 4.3 — R1 airway ───────────────────────────────────────────
    { key: "r1-sd", page: 1, x: 184, y: 486 },
    { key: "r1-nfp", page: 1, x: 206, y: 486 },
    { key: "r1-ofp", page: 1, x: 236, y: 486 },
    { key: "r1-ngp", page: 1, x: 265, y: 486 },
    { key: "r1-ett", page: 1, x: 184, y: 475 },
    { key: "r1-cricothyrotomy", page: 1, x: 229, y: 475 },

    // ── Section 4.4 — R1 breathing ───────────────────────────────────────
    { key: "r1-thoracocentesis", page: 1, x: 184, y: 454 },
    { key: "r1-shvl", page: 1, x: 256, y: 454 },
    { key: "r1-thoracoRightBlood", page: 1, x: 192, y: 432 },
    { key: "r1-thoracoRightAir", page: 1, x: 248, y: 432 },
    { key: "r1-thoracoLeftBlood", page: 1, x: 192, y: 421 },
    { key: "r1-thoracoLeftAir", page: 1, x: 248, y: 421 },

    // ── Section 4.5 — R1 access ───────────────────────────────────────────
    { key: "r1-pvd", page: 1, x: 339, y: 486 },
    { key: "r1-cvd", page: 1, x: 367, y: 486 },
    { key: "r1-ioShoulder", page: 1, x: 322, y: 475 },
    { key: "r1-ioTibia", page: 1, x: 354, y: 475 },
    { key: "r1-ioSternum", page: 1, x: 393, y: 475 },

    // ── Section 4.6 — R1 hypothermia prevention ──────────────────────────
    { key: "r1-hypothermiaActive", page: 1, x: 305, y: 442 },
    { key: "r1-hypothermiaPassive", page: 1, x: 320, y: 442 },

    // ── Section 4.7 — R1 immobilization ──────────────────────────────────
    { key: "r1-immobCervical", page: 1, x: 305, y: 410 },
    { key: "r1-immobSpine", page: 1, x: 335, y: 410 },
    { key: "r1-immobPelvis", page: 1, x: 372, y: 410 },
    { key: "r1-immobRUE", page: 1, x: 305, y: 399 },
    { key: "r1-immobLUE", page: 1, x: 332, y: 399 },
    { key: "r1-immobRLE", page: 1, x: 360, y: 399 },
    { key: "r1-immobLLE", page: 1, x: 389, y: 399 },

    // ── Section 4.8 — R1 urinary catheter / gastric tube ─────────────────
    { key: "r1-urinaryCatheter", page: 1, x: 457, y: 486 },
    { key: "r1-gastricTube", page: 1, x: 457, y: 475 },

    // ── Section 4.9 — R1 REBOA ───────────────────────────────────────────
    { key: "r1-reboa", page: 1, x: 535, y: 486 },

    // ── Section 4.10 — R1 decontamination ────────────────────────────────
    { key: "r1-decontamination", page: 1, x: 457, y: 455 },

    // ── Section 4.11 — R1 anesthesia ─────────────────────────────────────
    { key: "r1-anesthesia", page: 1, x: 496, y: 434 },
    { key: "r1-anesthesiaRegional", page: 1, x: 457, y: 424 },

    // ── Section 4.12 — R1 eye shield ─────────────────────────────────────
    { key: "r1-eyeShieldRight", page: 1, x: 511, y: 399 },
    { key: "r1-eyeShieldLeft", page: 1, x: 525, y: 399 },

    // ── Section 3.1 — Trauma type ─────────────────────────────────────────
    { key: "p1-trauma-gunshot", page: 1, x: 367, y: 719 },
    { key: "p1-trauma-explosive", page: 1, x: 428, y: 719 },
    { key: "p1-trauma-blunt", page: 1, x: 477, y: 719 },
    { key: "p1-trauma-open", page: 1, x: 510, y: 719 },
    { key: "p1-trauma-crush", page: 1, x: 367, y: 708 },
    { key: "p1-trauma-penetrating", page: 1, x: 472, y: 708 },
    { key: "p1-trauma-nonpenetrating", page: 1, x: 523, y: 708 },
    { key: "p1-prior-assessWarm", page: 1, x: 367, y: 698 },
    { key: "p1-prior-assessCold", page: 1, x: 412, y: 698 },

    // ── Section 3.2 — Condition type ─────────────────────────────────────
    { key: "p1-condition-somatic", page: 1, x: 366, y: 677 },
    { key: "p1-condition-infectious", page: 1, x: 366, y: 666 },
    { key: "p1-condition-psychiatric", page: 1, x: 366, y: 654 },
    { key: "p1-condition-poisoning", page: 1, x: 366, y: 636 },

    // ── Section 3.3 — CBRN ───────────────────────────────────────────────
    { key: "p1-cbrn-chemical", page: 1, x: 370, y: 602 },
    { key: "p1-cbrn-biological", page: 1, x: 370, y: 592 },
    { key: "p1-cbrn-radiological", page: 1, x: 370, y: 582 },
    { key: "p1-cbrn-nuclear", page: 1, x: 370, y: 572 },

    // ── Section 9 — Diagnostics checkboxes ───────────────────────────────
    // E-FAST radio group; Ro, Otoscopy P/L, ECG presence checkboxes
    { key: "p1-diag-efast-pos",     page: 1, x: 60,  y: 285 },
    { key: "p1-diag-efast-neg",     page: 1, x: 79,  y: 285 },
    { key: "p1-diag-efast-unk",     page: 1, x: 95,  y: 285 },
    { key: "p1-diag-xray-cb",       page: 1, x: 155, y: 285 },
    { key: "p1-diag-otoscopy-r-cb", page: 1, x: 340, y: 285 },
    { key: "p1-diag-otoscopy-l-cb", page: 1, x: 429, y: 285 },
    { key: "p1-diag-pupils-cb",     page: 1, x: 553, y: 286 },
    { key: "p1-diag-ecg-cb",        page: 1, x: 155, y: 273 },

    // ── Section 10.2 — Tetanus prophylaxis ───────────────────────────────
    { key: "r1-tetanusAdministered", page: 1, x: 25, y: 179 },

    // ── Section 10.2 — Blood transfusion ─────────────────────────────────
    { key: "r1-care-bloodTransfused", page: 1, x: 304, y: 179 },

    // ── Section 10.3 — Blood type unknown ────────────────────────────────
    { key: "blood-type-unknown", page: 1, x: 464, y: 179 },

    // ── Between sections 10–11 — Kit 1 / Kit 2 ───────────────────────────
    { key: "r1-kit1", page: 1, x: 472, y: 132 },
    { key: "r1-kit2", page: 1, x: 527, y: 132 },

    // ── Section 13 — Evacuation category ─────────────────────────────────
    { key: "evac-cat-I", page: 1, x: 125, y: 51 },
    { key: "evac-cat-II", page: 1, x: 138, y: 51 },
    { key: "evac-cat-III", page: 1, x: 153, y: 51 },

    // ── Section 13 — Discharge transport type ────────────────────────────
    { key: "discharge-transport-casevac", page: 1, x: 226, y: 51 },
    { key: "discharge-transport-mmpm", page: 1, x: 267, y: 51 },
    { key: "discharge-transport-medevac", page: 1, x: 305, y: 51 },

    // ── Section 13 — Vehicle type ─────────────────────────────────────────
    { key: "discharge-vehicle-A", page: 1, x: 397, y: 51 },
    { key: "discharge-vehicle-B", page: 1, x: 413, y: 51 },
    { key: "discharge-vehicle-C", page: 1, x: 430, y: 51 },
    { key: "discharge-vehicle-helicopter", page: 1, x: 448, y: 51 },
    { key: "discharge-vehicle-boat", page: 1, x: 476, y: 51 },
    { key: "discharge-vehicle-train", page: 1, x: 500, y: 51 },

    // ── Section 13 — Discharge position ──────────────────────────────────
    { key: "discharge-position-sitting", page: 1, x: 82, y: 40 },
    { key: "discharge-position-lying", page: 1, x: 120, y: 40 },

    // ── Section 13 — Escort ───────────────────────────────────────────────
    { key: "discharge-escort-doctor", page: 1, x: 209, y: 40 },
    { key: "discharge-escort-nurse", page: 1, x: 231, y: 40 },

    // ── Section 13 — Discharge equipment ─────────────────────────────────
    { key: "discharge-equip-oxygen", page: 1, x: 399, y: 40 },
    { key: "discharge-equip-ventilator", page: 1, x: 437, y: 40 },

    // ── Section 13 — Discharge role ───────────────────────────────────────
    { key: "discharge-role-1", page: 1, x: 519, y: 65 },
    { key: "discharge-role-2", page: 1, x: 533, y: 65 },
    { key: "discharge-role-3", page: 1, x: 547, y: 65 },
    { key: "discharge-role-4", page: 1, x: 562, y: 65 },

    // ── Section 14 — Medic role (Роль 1–4) ───────────────────────────────
    { key: "medic-role-1", page: 1, x: 521, y: 24 },
    { key: "medic-role-2", page: 1, x: 534, y: 24 },
    { key: "medic-role-3", page: 1, x: 549, y: 24 },
    { key: "medic-role-4", page: 1, x: 563, y: 24 },

    // ════════════════════════════════════════════════════════════════════════
    // PAGE 2
    // ════════════════════════════════════════════════════════════════════════

    // ── Section 15 — R2 triage category ──────────────────────────────────
    { key: "r2-triage-I", page: 2, x: 503, y: 801 },
    // ── Section 16 — Prior care on role 1 ────────────────────────────────
    { key: "r2-prior-care-given",   page: 2, x: 191, y: 789 },
    { key: "r2-prior-care-unknown", page: 2, x: 245, y: 789 },
    { key: "r2-prior-care-none",    page: 2, x: 290, y: 790 },

    // ── Section 16 — R2 patient examination (Огляд пацієнта) ─────────────
    // Left column: Airway (Дихальні шляхи)
    { key: "r2-exam-airway-patent",           page: 2, x: 90,  y: 759 },  // Прохідні
    { key: "r2-exam-airway-obstruct-partial", page: 2, x: 75,  y: 747 },  // Обструкція Часткова
    { key: "p2-airway-device-NPA",            page: 2, x: 120, y: 747 },  // НПА пристрій
    { key: "r2-exam-airway-ett",              page: 2, x: 62,  y: 726 },  // ЗПДШ ETT in place
    { key: "r2-exam-airway-lma",              page: 2, x: 98,  y: 726 },  // ЗПДШ LMA in place
    { key: "p2-exam-airway-present",          page: 2, x: 26,  y: 738 },  // ЗПДШ наявний
    { key: "r2-exam-resp-support",            page: 2, x: 26,  y: 715 },  // Respiratory support checkbox
    // Breathing (Дихання)
    // Breath sounds (Дихальні шуми) — Везикулярне/бронхіальне П/Л
    { key: "r2-exam-sounds-vb-r",     page: 2, x: 123, y: 694 },
    { key: "r2-exam-sounds-vb-l",     page: 2, x: 139, y: 693 },
    // Ослаблені П/Л
    { key: "r2-exam-sounds-weak-r",   page: 2, x: 67,  y: 662 },
    { key: "r2-exam-sounds-weak-l",   page: 2, x: 82,  y: 663 },
    // Сухі хрипи П/Л
    { key: "r2-exam-sounds-dry-r",    page: 2, x: 67,  y: 673 },
    { key: "r2-exam-sounds-dry-l",    page: 2, x: 82,  y: 674 },
    // Вологі хрипи П/Л
    { key: "r2-exam-sounds-wet-r",    page: 2, x: 76,  y: 683 },
    { key: "r2-exam-sounds-wet-l",    page: 2, x: 92,  y: 683 },
    // Відсутні П/Л
    { key: "r2-exam-sounds-absent-r", page: 2, x: 60,  y: 652 },
    { key: "r2-exam-sounds-absent-l", page: 2, x: 75,  y: 652 },

    // Middle-left column: Skin (Шкіра)
    { key: "r2-exam-skin-warm",     page: 2, x: 194, y: 758 },  // Тепла
    { key: "r2-exam-skin-cold",     page: 2, x: 226, y: 758 },  // Холодна
    { key: "r2-exam-skin-pink",     page: 2, x: 166, y: 747 },  // Рожева
    { key: "r2-exam-skin-pale",     page: 2, x: 205, y: 748 },  // Бліда
    { key: "r2-exam-skin-cyanotic", page: 2, x: 236, y: 747 },  // Ціанотична
    { key: "r2-exam-skin-dry",      page: 2, x: 167, y: 738 },  // Суха
    { key: "r2-exam-skin-moist",    page: 2, x: 194, y: 738 },  // Волога
    { key: "r2-exam-skin-rash",     page: 2, x: 167, y: 727 },  // Висипка
    { key: "r2-exam-skin-edema",    page: 2, x: 206, y: 728 },  // Набряки
    // Peripheral pulse (Периферичний пульс)
    { key: "r2-exam-pulse-present", page: 2, x: 167, y: 705 },  // Наявний
    { key: "r2-exam-pulse-weak",    page: 2, x: 207, y: 705 },  // Слабкий
    { key: "r2-exam-pulse-absent",  page: 2, x: 167, y: 695 },  // Відсутній
    // Capillary refill (Капілярне наповнення)
    { key: "r2-exam-caprefill-fast", page: 2, x: 166, y: 674 },  // ≤2 с
    { key: "r2-exam-caprefill-slow", page: 2, x: 188, y: 674 },  // >2 с
    // Heart sounds (Тони серця)
    { key: "r2-exam-heart-clear",   page: 2, x: 166, y: 653 },  // Ясні
    { key: "r2-exam-heart-muffled", page: 2, x: 192, y: 653 },  // Приглушені

    // Middle-right column: Skull / Trachea / Jugular / Chest
    // Skull integrity (Порушення цілісності черепа)
    { key: "r2-exam-skull-no",  page: 2, x: 294, y: 749 },  // Ні
    { key: "r2-exam-skull-yes", page: 2, x: 311, y: 749 },  // Так
    // CSF leak (Лікворея)
    { key: "r2-exam-csf-no",  page: 2, x: 369, y: 748 },
    { key: "r2-exam-csf-yes", page: 2, x: 386, y: 749 },
    // Cervical spine injury (Підозра на травму ШВХ)
    { key: "r2-exam-spine-no",  page: 2, x: 383, y: 737 },
    { key: "r2-exam-spine-yes", page: 2, x: 400, y: 737 },
    // Trachea deviation (Відхилення по середній лінії)
    { key: "r2-exam-trachea-r", page: 2, x: 397, y: 716 },  // П
    { key: "r2-exam-trachea-l", page: 2, x: 412, y: 716 },  // Л
    // Jugular veins collapsed (Спалі) — y:702 row
    { key: "r2-exam-jvc-no",  page: 2, x: 369, y: 705 },
    { key: "r2-exam-jvc-yes", page: 2, x: 386, y: 704 },
    // Jugular veins distended (Розширені) — y:693 row
    { key: "r2-exam-jvd-no",  page: 2, x: 337, y: 695 },
    { key: "r2-exam-jvd-yes", page: 2, x: 354, y: 695 },
    // Chest (Грудна клітка)
    { key: "r2-exam-chest-symmetric",       page: 2, x: 349, y: 681 },  // Симетрична
    { key: "r2-exam-chest-asymm-r",         page: 2, x: 352, y: 671 },  // Несиметрична П
    { key: "r2-exam-chest-asymm-l",         page: 2, x: 368, y: 671 },  // Л
    { key: "r2-exam-chest-flail-r",         page: 2, x: 334, y: 662 },  // Флотація П
    { key: "r2-exam-chest-flail-l",         page: 2, x: 350, y: 662 },  // Л
    { key: "r2-exam-chest-penetrating-no",  page: 2, x: 365, y: 653 },  // Проникна Ні
    { key: "r2-exam-chest-penetrating-yes", page: 2, x: 382, y: 653 },  // Так

    // Right column: Abdomen / Pelvis / Limbs / Neuro / Eyes
    // Abdomen shape (Живіт Форма)
    { key: "r2-exam-abdomen-normal",    page: 2, x: 490, y: 758 },  // Без змін
    { key: "r2-exam-abdomen-distended", page: 2, x: 529, y: 758 },  // Здутий
    // Palpation (Пальпація)
    { key: "r2-exam-palp-nontender", page: 2, x: 477, y: 748 },  // Безболісний
    { key: "r2-exam-palp-tender",    page: 2, x: 531, y: 748 },  // Болючий
    // Peritoneal irritation (Подразнення очеревини)
    { key: "r2-exam-peritonism-no",  page: 2, x: 529, y: 738 },
    { key: "r2-exam-peritonism-yes", page: 2, x: 546, y: 738 },
    // Abdominal penetrating trauma (Проникна травма)
    { key: "r2-exam-abd-penetrating-no",  page: 2, x: 500, y: 729 },
    { key: "r2-exam-abd-penetrating-yes", page: 2, x: 519, y: 729 },
    // Pelvis stable (Таз стабільний)
    { key: "r2-exam-pelvis-stable-yes", page: 2, x: 497, y: 720 },
    { key: "r2-exam-pelvis-stable-no",  page: 2, x: 516, y: 720 },
    // Limb deformity (Кінцівки Деформація)
    { key: "r2-exam-limb-deformity-no",  page: 2, x: 523, y: 709 },
    { key: "r2-exam-limb-deformity-yes", page: 2, x: 541, y: 709 },
    // Limb locations (ПВК/ЛВК/ПНК/ЛНК)
    { key: "r2-exam-limb-rue", page: 2, x: 437, y: 701 },
    { key: "r2-exam-limb-lue", page: 2, x: 464, y: 701 },
    { key: "r2-exam-limb-rle", page: 2, x: 489, y: 701 },
    { key: "r2-exam-limb-lle", page: 2, x: 516, y: 701 },
    // Neurological deficit (Неврологічний дефіцит)
    { key: "r2-exam-neuro-deficit-no",  page: 2, x: 523, y: 690 },
    { key: "r2-exam-neuro-deficit-yes", page: 2, x: 541, y: 691 },
    // Neuro deficit locations
    { key: "r2-exam-neuro-rue", page: 2, x: 437, y: 682 },
    { key: "r2-exam-neuro-lue", page: 2, x: 464, y: 682 },
    { key: "r2-exam-neuro-rle", page: 2, x: 489, y: 682 },
    { key: "r2-exam-neuro-lle", page: 2, x: 516, y: 681 },
    // Eye light reaction (Реакція на світло Так S D / Ні S D)
    { key: "r2-exam-light-yes-s",   page: 2, x: 513, y: 662 },  // Так S
    { key: "r2-exam-light-yes-d",   page: 2, x: 525, y: 661 },  // Так D
    { key: "r2-exam-light-no-s",    page: 2, x: 550, y: 662 },  // Ні S
    { key: "r2-exam-light-no-d",    page: 2, x: 563, y: 662 },  // Ні D
    // ── Section 17 — R2 diagnostics checkboxes ───────────────────────────
    { key: "r2-diag-efast-pos", page: 2, x: 60,  y: 598 },
    { key: "r2-diag-efast-neg", page: 2, x: 79,  y: 598 },
    { key: "r2-diag-efast-unk", page: 2, x: 95,  y: 598 },
    { key: "r2-diag-ro",        page: 2, x: 141, y: 599 },
    { key: "r2-diag-ct",        page: 2, x: 163, y: 599 },
    { key: "r2-diag-ecg-cb",         page: 2, x: 185, y: 599 },
    { key: "r2-diag-us",             page: 2, x: 211, y: 599 },
    { key: "r2-diag-otoscopy-r-cb",  page: 2, x: 181, y: 588 },
    { key: "r2-diag-otoscopy-l-cb",  page: 2, x: 221, y: 588 },
    { key: "r2-triage-II", page: 2, x: 519, y: 801 },
    { key: "r2-triage-III", page: 2, x: 536, y: 801 },
    { key: "r2-triage-IV", page: 2, x: 557, y: 801 },

    // ── Section 18.1 — R2 tourniquet limbs ───────────────────────────────
    { key: "r2-tourniquetRUE", page: 2, x: 24, y: 534 },
    { key: "r2-tourniquetLUE", page: 2, x: 24, y: 523 },
    { key: "r2-tourniquetRLE", page: 2, x: 24, y: 511 },
    { key: "r2-tourniquetLLE", page: 2, x: 24, y: 500 },
    // ── Section 18.2 — R2 airway ─────────────────────────────────────────
    { key: "r2-o2", page: 2, x: 133, y: 544 },
    { key: "r2-nfp", page: 2, x: 225, y: 544 },
    { key: "r2-ofp", page: 2, x: 255, y: 544 },
    { key: "r2-shvl", page: 2, x: 196, y: 544 },
    { key: "r2-ngp", page: 2, x: 133, y: 534 },
    { key: "r2-ett", page: 2, x: 158, y: 534 },
    { key: "r2-cricothyrotomy", page: 2, x: 207, y: 534 },

    // ── Section 18.3 — R2 occlusive / needle decompression ───────────────
    { key: "r2-occlusiveRight", page: 2, x: 205, y: 520 },
    { key: "r2-occlusiveLeft", page: 2, x: 219, y: 520 },
    { key: "r2-needleDecompRight", page: 2, x: 213, y: 511 },
    { key: "r2-needleDecompLeft", page: 2, x: 227, y: 511 },

    // ── Section 18.4 — R2 access ─────────────────────────────────────────
    { key: "r2-pvd", page: 2, x: 163, y: 497 },
    { key: "r2-cvd", page: 2, x: 190, y: 497 },
    { key: "r2-arterial", page: 2, x: 217, y: 497 },
    { key: "r2-ioShoulder", page: 2, x: 177, y: 486 },
    { key: "r2-ioTibia", page: 2, x: 207, y: 486 },
    { key: "r2-ioSternum", page: 2, x: 243, y: 486 },

    // ── Section 18.5 — R2 hypothermia prevention ─────────────────────────
    { key: "r2-hypothermiaActive", page: 2, x: 227, y: 473 },
    { key: "r2-hypothermiaPassive", page: 2, x: 242, y: 473 },

    // ── Section 18.6 — R2 immobilization ─────────────────────────────────
    { key: "r2-immobCervical", page: 2, x: 350, y: 531 },
    { key: "r2-immobSpine", page: 2, x: 376, y: 531 },
    { key: "r2-immobPelvis", page: 2, x: 411, y: 531 },
    { key: "r2-immobRUE", page: 2, x: 298, y: 521 },
    { key: "r2-immobLUE", page: 2, x: 322, y: 521 },
    { key: "r2-immobRLE", page: 2, x: 347, y: 521 },
    { key: "r2-immobLLE", page: 2, x: 372, y: 521 },

    // ── Section 18.7 — R2 wound care ─────────────────────────────────────
    { key: "r2-bandage", page: 2, x: 24, y: 486 },
    { key: "r2-packing", page: 2, x: 24, y: 477 },

    // ── Section 18.8 — R2 urinary catheter / gastric tube / eye shield ───
    { key: "r2-urinaryCatheter", page: 2, x: 298, y: 544 },
    { key: "r2-gastricTube", page: 2, x: 369, y: 544 },
    { key: "r2-eyeShieldRight", page: 2, x: 509, y: 544 },
    { key: "r2-eyeShieldLeft", page: 2, x: 524, y: 544 },

    // ── Section 18.9 — R2 decontamination ────────────────────────────────
    { key: "r2-decontamination", page: 2, x: 456, y: 531 },

    // ── Section 18.10 — Surgery types ────────────────────────────────────
    { key: "surg-craniectomy", page: 2, x: 298, y: 507 },
    { key: "surg-cantholysis", page: 2, x: 357, y: 507 },
    { key: "surg-tracheostomy", page: 2, x: 446, y: 507 },
    { key: "surg-thoracotomy", page: 2, x: 507, y: 507 },
    { key: "surg-pericardiotomy", page: 2, x: 298, y: 497 },
    { key: "surg-thoracocentesis",      page: 2, x: 370, y: 497 },
    { key: "r2care-thoracoRightBlood",  page: 2, x: 440, y: 497 },
    { key: "r2care-thoracoRightAir",    page: 2, x: 467, y: 497 },
    { key: "r2care-thoracoLeftBlood",   page: 2, x: 516, y: 498 },
    { key: "r2care-thoracoLeftAir",     page: 2, x: 543, y: 497 },
    { key: "surg-laparotomy", page: 2, x: 298, y: 487 },
    { key: "surg-azf", page: 2, x: 356, y: 487 },
    { key: "surg-amputation", page: 2, x: 383, y: 487 },
    { key: "surg-fasciotomy", page: 2, x: 464, y: 487 },
    { key: "surg-escharotomy", page: 2, x: 521, y: 487 },
    { key: "surg-vascularShunt", page: 2, x: 298, y: 477 },

    // ── Section 18.11 — REBOA ─────────────────────────────────────────────
    { key: "surg-reboa", page: 2, x: 298, y: 463 },

    // ── Section 18.12 — Wound closure / debridement ───────────────────────
    { key: "surg-suturing", page: 2, x: 355, y: 463 },
    { key: "surg-debridement", page: 2, x: 433, y: 463 },

    // ── Section 18.14 — Anesthesia type ──────────────────────────────────
    { key: "surg-anesthesia-local", page: 2, x: 90, y: 384 },
    { key: "surg-anesthesia-regional", page: 2, x: 136, y: 384 },
    { key: "surg-anesthesia-general", page: 2, x: 188, y: 384 },
    { key: "surg-anesthesia-sedation", page: 2, x: 232, y: 384 },

    // ── Section 23.1 — R2 tetanus prophylaxis ────────────────────────────
    { key: "r2-tetanusAdministered", page: 2, x: 25, y: 179 },

    // ── Section 23.1 — R2 blood transfused ───────────────────────────────
    { key: "r2care-bloodTransfused", page: 2, x: 304, y: 179 },

    // ── Section 23.2 — Blood type unknown (p2) ───────────────────────────
    { key: "p2-blood-type-unknown", page: 2, x: 464, y: 179 },

    // ── Kit 1 / Kit 2 (R2) ───────────────────────────────────────────────
    { key: "r2-kit1", page: 2, x: 472, y: 132 },
    { key: "r2-kit2", page: 2, x: 527, y: 132 },

    // ── Section 26 — R2 evacuation category ──────────────────────────────
    { key: "r2-evac-cat-I", page: 2, x: 125, y: 51 },
    { key: "r2-evac-cat-II", page: 2, x: 138, y: 51 },
    { key: "r2-evac-cat-III", page: 2, x: 153, y: 51 },

    // ── Section 26 — R2 discharge transport type ──────────────────────────
    { key: "r2-discharge-transport-casevac", page: 2, x: 226, y: 51 },
    { key: "r2-discharge-transport-mmpm", page: 2, x: 267, y: 51 },
    { key: "r2-discharge-transport-medevac", page: 2, x: 305, y: 51 },

    // ── Section 26 — R2 vehicle type ─────────────────────────────────────
    { key: "r2-discharge-vehicle-A", page: 2, x: 397, y: 51 },
    { key: "r2-discharge-vehicle-B", page: 2, x: 413, y: 51 },
    { key: "r2-discharge-vehicle-C", page: 2, x: 430, y: 51 },
    { key: "r2-discharge-vehicle-helicopter", page: 2, x: 448, y: 51 },
    { key: "r2-discharge-vehicle-boat", page: 2, x: 476, y: 51 },
    { key: "r2-discharge-vehicle-train", page: 2, x: 500, y: 51 },

    // ── Section 26 — R2 discharge position ───────────────────────────────
    { key: "r2-discharge-position-sitting", page: 2, x: 82, y: 40 },
    { key: "r2-discharge-position-lying", page: 2, x: 120, y: 40 },

    // ── Section 26 — R2 escort ────────────────────────────────────────────
    { key: "r2-discharge-escort-doctor", page: 2, x: 209, y: 40 },
    { key: "r2-discharge-escort-nurse", page: 2, x: 231, y: 40 },

    // ── Section 26 — R2 discharge equipment ──────────────────────────────
    { key: "r2-discharge-equip-oxygen", page: 2, x: 399, y: 40 },
    { key: "r2-discharge-equip-ventilator", page: 2, x: 437, y: 40 },

    // ── Section 26 — R2 discharge role (Роль at y:63) ────────────────────
    { key: "r2-discharge-role-1", page: 2, x: 521, y: 65 },
    { key: "r2-discharge-role-2", page: 2, x: 535, y: 65 },
    { key: "r2-discharge-role-3", page: 2, x: 549, y: 65 },
    { key: "r2-discharge-role-4", page: 2, x: 564, y: 65 },
    // ── Section 27 — R2 medic role (Роль at y:22) ────────────────────────
    { key: "r2-medic-role-1", page: 2, x: 521, y: 24 },
    { key: "r2-medic-role-2", page: 2, x: 534, y: 24 },
    { key: "r2-medic-role-3", page: 2, x: 549, y: 24 },
    { key: "r2-medic-role-4", page: 2, x: 563, y: 24 },
  ],
  importAll: async (rawFields, ids, dispatch, rpc, createUuid) => {
    const fields = applyJoinFields(rawFields, form001.joinFields);
    await importForm001Fields(form001, fields, ids, dispatch, rpc, createUuid);
  },
};
